import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { addStage, archiveStage, getPipelines, renameStage, reorderStages, setStageNurture } from '@/data/client';
import { FormError } from '@/components/crm/Modal';

/* Stages of the default pipeline. Open stages can be added, renamed, reordered
   and archived (when empty); Won and Lost can only be renamed — they carry the
   revenue rules and always sit at the end. */
export function PipelineSettings() {
  const qc = useQueryClient();
  const { data: pipelines } = useQuery({ queryKey: ['pipelines'], queryFn: getPipelines });
  const pipeline = pipelines?.find((p) => p.isDefault) ?? pipelines?.[0];
  const [newName, setNewName] = useState('');
  const refresh = () => { for (const k of ['pipelines', 'deals', 'audit']) void qc.invalidateQueries({ queryKey: [k] }); };
  const rename = useMutation({ mutationFn: (v: { id: string; name: string }) => renameStage(v.id, v.name), onSuccess: refresh });
  const order = useMutation({ mutationFn: reorderStages, onSuccess: refresh });
  const archive = useMutation({ mutationFn: archiveStage, onSuccess: refresh });
  const nurture = useMutation({ mutationFn: (v: { id: string; on: boolean }) => setStageNurture(v.id, v.on), onSuccess: refresh });
  const add = useMutation({ mutationFn: () => addStage(newName), onSuccess: () => { setNewName(''); refresh(); } });
  if (!pipeline) return null;
  const open = pipeline.stages.filter((s) => s.kind === 'open');
  const terminal = pipeline.stages.filter((s) => s.kind !== 'open');
  const move = (i: number, dir: -1 | 1) => {
    const ids = open.map((s) => s.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    order.mutate(ids);
  };

  return (
    <Card padding={false} className="crm-section crm-settings-card">
      <div className="crm-section__head"><h3>{pipeline.name}</h3></div>
      <ol className="crm-stages">
        {open.map((s, i) => (
          <li key={s.id}>
            <span className="crm-stages__n">{i + 1}</span>
            <StageName id={s.id} name={s.name} onSave={(name) => rename.mutate({ id: s.id, name })} />
            <label className="crm-stages__nurture" title="Nurture: deals stay alive but out of pipeline value and stale alerts; new deals never start here">
              <input type="checkbox" checked={s.nurture} disabled={nurture.isPending} onChange={(e) => nurture.mutate({ id: s.id, on: e.target.checked })} /> Nurture
            </label>
            <button type="button" className="lead-modal__x" aria-label="Move up" disabled={i === 0 || order.isPending} onClick={() => move(i, -1)}><Icon name="chevron-down" size={16} style={{ transform: 'rotate(180deg)' }} /></button>
            <button type="button" className="lead-modal__x" aria-label="Move down" disabled={i === open.length - 1 || order.isPending} onClick={() => move(i, 1)}><Icon name="chevron-down" size={16} /></button>
            <button type="button" className="lead-modal__x" aria-label={`Remove ${s.name}`} onClick={() => archive.mutate(s.id)}><Icon name="trash" size={15} /></button>
          </li>
        ))}
        {terminal.map((s) => (
          <li key={s.id} className="crm-stages--end">
            <span className="crm-stages__n"><Icon name={s.kind === 'won' ? 'award' : 'x'} size={13} /></span>
            <StageName id={s.id} name={s.name} onSave={(name) => rename.mutate({ id: s.id, name })} />
            <span className="crm-cell-sub">{s.kind === 'won' ? 'Requires a value · locks attribution' : 'Requires a reason'}</span>
          </li>
        ))}
      </ol>
      <form className="crm-addopt" style={{ marginTop: 14 }} onSubmit={(e) => { e.preventDefault(); if (newName.trim()) add.mutate(); }}>
        <Input label="Add a stage" placeholder="Stage name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <Button type="submit" size="sm" disabled={!newName.trim() || add.isPending}>Add</Button>
      </form>
      <FormError error={rename.error ?? order.error ?? archive.error ?? add.error ?? nurture.error} />
    </Card>
  );
}

/* Inline rename: edits locally, saves on Enter or blur if changed. */
export function StageName({ id, name, onSave }: { id: string; name: string; onSave: (name: string) => void }) {
  const [v, setV] = useState(name);
  const commit = () => { const t = v.trim(); if (t && t !== name) onSave(t); else setV(name); };
  return (
    <input key={id} className="crm-inline-input" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setV(name); }} aria-label="Name" />
  );
}
